import React from 'react';
import Input from '../../components/common/Input';
import Label from '../../components/common/Label';
import ErrorMessage from '../../components/common/ErrorMessage';
import ValidationIcon from '../../components/common/ValidateIcon';
import AlertIcon from '../../components/common/Icons/AlertIcon';

/**
 * EmailInput Component
 *
 * Renders an email input field with label and error handling.
 * Applies error-specific styles and icons when validation fails.
 * Implements proper ref forwarding for accessibility and focus management.
 *
 * Props:
 * - value: The current value of the email input.
 * - onChange: Callback invoked when the email input changes.
 * - onBlur: Callback triggered when the input field loses focus, used for validation.
 * - error: Error message string for email validation.
 * - errorRef: Ref for the error message element for accessibility.
 */
const EmailInput = React.forwardRef(({ value, onChange, onBlur, error, errorRef }, ref) => {
  const isEmailInvalid = Boolean(error);

  const borderClass = isEmailInvalid ? 'border-red-500' : '';
  const focusClass = isEmailInvalid ? '!focus:ring-0 !focus:border-red-500' : '';

  return (
    <div className="w-full">
      <Label variant="dark" htmlFor="email" customClass="block text-sm font-normal">
        Email Address
      </Label>

      <div className="relative">
        <Input
          type="email"
          id="email"
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder="Enter Your Email"
          variant="tertiary"
          ref={ref}
          customClass={borderClass}
          focusClass={focusClass || undefined}
        />

        {isEmailInvalid && (
          <>
            <div className="absolute right-3 top-4">
              <ValidationIcon isValid={false} customErrorIcon={AlertIcon} />
            </div>

            <ErrorMessage message={error} className="!static mt-2 text-red-500" ref={errorRef} />
          </>
        )}
      </div>
    </div>
  );
});

export default EmailInput;
